import { Injectable, computed, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Product } from '../product.model';

export interface CartItem {
  product: Product;
  quantity: number;
}

export interface CartSummary {
  items: CartItem[];
  totalItems: number;
  subtotal: number;
  tax: number;
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly STORAGE_KEY = 'cart'; // Changed to match checkout component
  private readonly TAX_RATE = 0.13; // 13% HST/GST for Ontario

  // ✅ Modern Angular Signals primary state
  private readonly _cartItems = signal<CartItem[]>([]);
  readonly items = this._cartItems.asReadonly();

  // Mini-cart drawer state
  private readonly _isCartDrawerOpen = signal<boolean>(false);
  readonly isCartDrawerOpen = this._isCartDrawerOpen.asReadonly();
  private lastOpenedTimestamp = 0;

  openCartDrawer(): void {
    this.lastOpenedTimestamp = Date.now();
    this._isCartDrawerOpen.set(true);
  }

  closeCartDrawer(): void {
    // Prevent immediate close if opened during the same click event
    if (Date.now() - this.lastOpenedTimestamp < 250) {
      return;
    }
    this._isCartDrawerOpen.set(false);
  }

  toggleCartDrawer(): void {
    this._isCartDrawerOpen.update(open => !open);
  }

  // Public observables for interop
  readonly cartItems$ = toObservable(this._cartItems);

  readonly itemCount = computed(() =>
    this.items().reduce((sum, item) => sum + item.quantity, 0)
  );

  readonly subtotal = computed(() =>
    this.items().reduce((sum, item) => {
      const priceInCents = parseFloat(item.product.products_price) || 0;
      return sum + (priceInCents * item.quantity);
    }, 0)
  );

  readonly tax = computed(() => this.subtotal() * this.TAX_RATE);

  readonly total = computed(() => this.subtotal() + this.tax());

  readonly summary = computed<CartSummary>(() => ({
    items: this.items(),
    totalItems: this.itemCount(),
    subtotal: this.subtotal(),
    tax: this.tax(),
    total: this.total()
  }));

  cartItemCount$ = this.cartItems$.pipe(
    map(items => items.reduce((sum, item) => sum + item.quantity, 0))
  );

  cartSubtotal$ = this.cartItems$.pipe(
    map(items => items.reduce((sum, item) => {
      // products_price is already in cents as a string
      const priceInCents = parseFloat(item.product.products_price) || 0;
      return sum + (priceInCents * item.quantity);
    }, 0))
  );

  cartTax$ = this.cartSubtotal$.pipe(
    map(subtotal => subtotal * this.TAX_RATE)
  );

  cartTotal$ = this.cartSubtotal$.pipe(
    map(subtotal => subtotal + (subtotal * this.TAX_RATE))
  );

  cartSummary$: Observable<CartSummary> = this.cartItems$.pipe(
    map(items => {
      const subtotal = items.reduce((sum, item) => {
        // products_price is already in cents as a string
        const priceInCents = parseFloat(item.product.products_price) || 0;
        return sum + (priceInCents * item.quantity);
      }, 0);

      const tax = subtotal * this.TAX_RATE;
      const total = subtotal + tax;
      const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

      return {
        items,
        totalItems,
        subtotal,
        tax,
        total
      };
    })
  );

  constructor() {
    console.log('CartService: Constructor - initializing');
    this.loadFromLocalStorage();
    console.log('CartService: Initial cart item count:', this._cartItems().length);

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key === this.STORAGE_KEY) {
          console.log('🔄 CartService: Storage event received for cart, reloading...');
          this.loadFromLocalStorage();
        }
      });
    }
  }

  /**
   * Add product to cart
   */
  addToCart(product: Product, quantity: number = 1): void {
    const current = this._cartItems();
    const existingIndex = current.findIndex(item =>
      String(item.product.products_id) === String(product.products_id)
    );

    let updated: CartItem[];
    if (existingIndex !== -1) {
      // Immutable update of quantity
      updated = current.map((item, index) =>
        index === existingIndex ? { ...item, quantity: item.quantity + quantity } : item
      );
    } else {
      // Add new product to cart
      updated = [...current, { product, quantity }];
    }

    this._cartItems.set(updated);
    this.saveToLocalStorage();
    this.notifyCartUpdate('added', product.products_name);
    this.openCartDrawer();
  }

  /**
   * Remove product from cart
   */
  removeFromCart(productId: number | string): void {
    const current = this._cartItems();
    const filtered = current.filter(item => String(item.product.products_id) !== String(productId));
    const removedItem = current.find(item => String(item.product.products_id) === String(productId));

    this._cartItems.set(filtered);
    this.saveToLocalStorage();

    if (removedItem) {
      this.notifyCartUpdate('removed', removedItem.product.products_name);
    }
  }

  /**
   * Update quantity of item in cart
   */
  updateQuantity(productId: number | string, quantity: number): void {
    if (quantity <= 0) {
      this.removeFromCart(productId);
      return;
    }

    const current = this._cartItems();
    const updated = current.map(item =>
      String(item.product.products_id) === String(productId) ? { ...item, quantity } : item
    );

    const found = current.find(item => String(item.product.products_id) === String(productId));
    if (found) {
      this._cartItems.set(updated);
      this.saveToLocalStorage();
      this.notifyCartUpdate('updated', found.product.products_name);
    }
  }

  /**
   * Clear entire cart
   */
  clearCart(): void {
    this._cartItems.set([]);
    this.saveToLocalStorage();
    this.notifyCartUpdate('cleared');
  }

  /**
   * Check if product is in cart
   */
  isInCart(productId: number | string): boolean {
    return this._cartItems().some(item => String(item.product.products_id) === String(productId));
  }

  /**
   * Get quantity of product in cart
   */
  getProductQuantity(productId: number | string): number {
    const item = this._cartItems().find(item => String(item.product.products_id) === String(productId));
    return item ? item.quantity : 0;
  }

  /**
   * Get cart item by product ID
   */
  getCartItem(productId: number | string): CartItem | undefined {
    return this._cartItems().find(item => String(item.product.products_id) === String(productId));
  }

  /**
   * Increment quantity
   */
  incrementQuantity(productId: number | string): void {
    const current = this.getProductQuantity(productId);
    this.updateQuantity(productId, current + 1);
  }

  /**
   * Decrement quantity
   */
  decrementQuantity(productId: number | string): void {
    const current = this.getProductQuantity(productId);
    if (current > 1) {
      this.updateQuantity(productId, current - 1);
    }
  }

  /**
   * Get cart for checkout
   */
  getCheckoutData(): CartItem[] {
    return this._cartItems();
  }

  /**
   * Set whole cart items directly
   */
  setCartItems(items: CartItem[]): void {
    this._cartItems.set(items);
    this.saveToLocalStorage();
    this.notifyCartUpdate('updated');
  }

  /**
   * Set cart from checkout flat format
   */
  setCartFromCheckout(checkoutItems: any[]): void {
    const items: CartItem[] = (checkoutItems || []).map((item: any) => {
      const { quantity, ...productData } = item;
      return {
        product: {
          ...productData,
          products_id: item.products_id,
          products_name: item.products_name,
          products_price: item.products_price != null ? String(item.products_price) : '0',
        },
        quantity: Number(quantity) || 1
      };
    });
    this._cartItems.set(items);
    this.saveToLocalStorage();
    this.notifyCartUpdate('updated');
  }

  /**
   * Save cart to localStorage in checkout-compatible format
   */
  private saveToLocalStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      // Transform to checkout format: flat structure with all product properties at root level
      const checkoutFormat = this._cartItems().map(item => ({
        ...item.product,
        products_id: item.product.products_id,
        products_name: item.product.products_name,
        products_price: item.product.products_price != null ? String(item.product.products_price) : '0',
        quantity: item.quantity
      }));

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(checkoutFormat));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }

  /**
   * Public method to reload cart from localStorage
   * Useful when cart is updated outside of CartService
   */
  public reloadFromLocalStorage(): void {
    console.log('🔄 CartService: Reloading cart from localStorage...');
    this.loadFromLocalStorage();
    console.log('✅ CartService: Reloaded, cart item count:', this._cartItems().length);
  }

  /**
   * Load cart from localStorage
   */
  private loadFromLocalStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      console.log('CartService: Loading from localStorage, key:', this.STORAGE_KEY);

      if (stored) {
        const checkoutItems = JSON.parse(stored);
        if (Array.isArray(checkoutItems)) {
          // Transform from checkout format back to CartItem format preserving all metadata
          const items: CartItem[] = checkoutItems.map((item: any) => {
            const { quantity, ...productData } = item;
            return {
              product: {
                ...productData,
                products_id: item.products_id,
                products_name: item.products_name,
                products_price: item.products_price != null ? String(item.products_price) : '0',
                products_image: item.products_image,
                products_model: item.products_model,
                products_description: item.products_description,
                products_quantity: item.products_quantity,
                products_status: item.products_status,
                products_price_old: item.products_price_old,
                categories_id: item.categories_id,
                categories_name: item.categories_name
              },
              quantity: Number(quantity) || 1
            };
          });

          console.log('CartService: Loaded cart items:', items.length);
          this._cartItems.set(items);
          return;
        }
      }
      this._cartItems.set([]);
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      localStorage.removeItem(this.STORAGE_KEY);
      this._cartItems.set([]);
    }
  }

  /**
   * Notify user of cart updates
   */
  private notifyCartUpdate(action: 'added' | 'removed' | 'updated' | 'cleared', productName?: string): void {
    // This can be connected to a notification service
    let message = '';

    switch (action) {
      case 'added':
        message = `${productName} added to cart`;
        break;
      case 'removed':
        message = `${productName} removed from cart`;
        break;
      case 'updated':
        message = `${productName} quantity updated`;
        break;
      case 'cleared':
        message = 'Cart cleared';
        break;
    }

    // Dispatch custom event for components to listen to
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cartUpdate', {
        detail: { action, productName, message }
      }));
    }
  }

  /**
   * Merge cart with user's saved cart (for logged-in users)
   */
  mergeWithUserCart(userCart: CartItem[]): void {
    const localCart = this._cartItems();
    const mergedMap = new Map<number, CartItem>();

    // Add local cart items
    localCart.forEach(item => {
      mergedMap.set(item.product.products_id, item);
    });

    // Merge with user cart (user cart takes precedence for quantities)
    userCart.forEach(item => {
      const existing = mergedMap.get(item.product.products_id);
      if (existing) {
        // Keep higher quantity
        existing.quantity = Math.max(existing.quantity, item.quantity);
      } else {
        mergedMap.set(item.product.products_id, item);
      }
    });

    const merged = Array.from(mergedMap.values());
    this._cartItems.set(merged);
    this.saveToLocalStorage();
  }

  /**
   * Validate cart items (check stock, prices, etc.)
   */
  async validateCart(): Promise<{ valid: boolean; errors: string[] }> {
    const errors: string[] = [];
    const items = this._cartItems();

    for (const item of items) {
      // Check if product is still available (assuming 'done' means available)
      if (!item.product.products_status || item.product.products_status === 'todo') {
        errors.push(`${item.product.products_name} is no longer available`);
      }

      // Check stock
      if (item.product.products_quantity !== undefined &&
          item.product.products_quantity < item.quantity) {
        errors.push(`Only ${item.product.products_quantity} units of ${item.product.products_name} available`);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}